<template>
  <div class="main-wrapper">
    <layoutheader></layoutheader>
    <breadcrumb-doctor title="My patients" text="Doctor" text1="My patients" />
    <div class="content">
      <div class="container">
        <div class="row">
          <doctorsidebar @doctor-image="doctorImage = $event"></doctorsidebar>
          <div class="col-md-7 col-lg-8 col-xl-9 adm-scope pts">

            <div class="pts-toolbar">
              <div class="adm-search pts-search">
                <i class="fas fa-search"></i>
                <input v-model="q" class="adm-input" placeholder="Search name, phone, email or ID" aria-label="Search patients" />
              </div>
              <select v-model="sort" class="adm-select pts-sort" aria-label="Sort" @change="load(1)">
                <option value="recent">Recently seen</option>
                <option value="upcoming">Upcoming first</option>
                <option value="visits">Most visits</option>
                <option value="name">Name A–Z</option>
              </select>
              <span class="adm-muted pts-count">{{ pagination.total }} patient{{ pagination.total === 1 ? "" : "s" }}</span>
            </div>

            <div v-if="error" class="adm-alert adm-alert--error"><i class="fas fa-exclamation-circle"></i><div>{{ error }}</div></div>

            <div v-if="loading && !patients.length" class="pts-grid">
              <div v-for="n in 6" :key="n" class="adm-card pts-card">
                <div class="adm-person"><span class="adm-avatar adm-avatar--lg"></span><div class="adm-skeleton" style="width: 60%"></div></div>
              </div>
            </div>
            <div v-else-if="!patients.length" class="adm-card adm-empty">
              <i class="fas fa-users"></i>
              <strong>{{ q ? "No patient matches your search" : "No patients yet" }}</strong>
              {{ q ? "Try part of the name, phone number or email." : "Patients appear here once they book with you." }}
            </div>

            <div v-else class="pts-grid" :class="{ 'is-loading': loading }">
              <article v-for="p in patients" :key="p.patientid" class="adm-card pts-card">
                <header class="pts-card__head">
                  <span class="adm-avatar adm-avatar--lg pts-avatar" :class="avatarTone(p)">
                    <img v-if="images[p.patientid]" :src="images[p.patientid]" :alt="name(p)" />
                    <template v-else>{{ initials(name(p)) }}</template>
                  </span>
                  <div class="pts-card__who">
                    <router-link :to="`/patient-profile/${p.patientid}`" class="pts-name">{{ name(p) }}</router-link>
                    <span class="adm-person__meta">{{ [`ID ${p.patientid}`, genderLabel(p.gender), p.age ? `${p.age} yrs` : ""].filter(Boolean).join(" · ") }}</span>
                    <span v-if="p.next_appointment" class="adm-badge adm-badge--info pts-next">
                      Next: {{ dayLabel(p.next_appointment) }}
                    </span>
                  </div>
                </header>

                <div class="pts-contact">
                  <a v-if="p.contactnumbers" :href="`tel:${p.contactnumbers}`" class="pts-contact__item"><i class="fas fa-phone-alt"></i>{{ p.contactnumbers }}</a>
                  <a v-if="p.email" :href="`mailto:${p.email}`" class="pts-contact__item" :title="p.email"><i class="fas fa-envelope"></i><span>{{ p.email }}</span></a>
                  <span v-if="!p.contactnumbers && !p.email" class="adm-muted pts-contact__item">No contact details</span>
                </div>

                <dl class="pts-stats">
                  <div><dt>Visits</dt><dd>{{ p.visits }}</dd></div>
                  <div><dt>No-shows</dt><dd :class="{ 'pts-warn': p.no_shows > 0 && p.no_shows >= p.visits }">{{ p.no_shows }}</dd></div>
                  <div><dt>Last seen</dt><dd class="pts-last">{{ p.last_appointment ? ago(p.last_appointment) : "—" }}</dd></div>
                </dl>

                <p class="pts-reason" :title="p.last_reason || ''">
                  <i class="fas fa-notes-medical"></i>
                  {{ p.last_reason || "No reason recorded" }}
                </p>

                <footer class="pts-actions">
                  <router-link :to="`/patient-profile/${p.patientid}`" class="adm-btn adm-btn--sm">Profile</router-link>
                  <button type="button" class="adm-btn adm-btn--sm adm-btn--primary" @click="book(p)">
                    <i class="fas fa-calendar-plus"></i> Book
                  </button>
                </footer>
              </article>
            </div>

            <div v-if="pagination.total_pages > 1" class="adm-pagination pts-pagination">
              <span>Page {{ pagination.page }} of {{ pagination.total_pages }}</span>
              <div class="adm-pagination__pages">
                <button type="button" class="adm-btn adm-btn--sm" :disabled="loading || !pagination.has_prev" @click="load(pagination.page - 1)"><i class="fas fa-arrow-left"></i> Previous</button>
                <button type="button" class="adm-btn adm-btn--sm" :disabled="loading || !pagination.has_next" @click="load(pagination.page + 1)">Next <i class="fas fa-arrow-right"></i></button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <doctorfooter />
  </div>
</template>

<script>
import axios from "axios";
import "../admin/admin.css";
import { errorMessage, initials } from "../admin/admin-utils";

const TONES = ["adm-tone-blue", "adm-tone-teal", "adm-tone-violet", "adm-tone-amber", "adm-tone-rose", "adm-tone-green"];

export default {
  name: "doctor-my-patients",
  data() {
    return {
      q: "",
      sort: "recent",
      patients: [],
      pagination: { total: 0, page: 1, per_page: 12, total_pages: 0, has_next: false, has_prev: false },
      loading: true,
      error: "",
      images: {},
      doctorImage: "",
      requestId: 0,
    };
  },
  computed: {
    doctorId() {
      return sessionStorage.getItem("userdbid");
    },
  },
  watch: {
    q() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.load(1), 350);
    },
  },
  methods: {
    initials,
    name(p) {
      return [p.firstname, p.lastname].filter(Boolean).join(" ").trim() || `Patient ${p.patientid}`;
    },
    genderLabel(g) {
      return { M: "Male", F: "Female", O: "Other" }[(g || "").trim()] || "";
    },
    avatarTone(p) {
      return TONES[p.patientid % TONES.length];
    },
    dayLabel(value) {
      const d = new Date(`${value}T00:00:00`);
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const diff = Math.round((d - today) / 86400000);
      if (diff === 0) return "Today";
      if (diff === 1) return "Tomorrow";
      return d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
    },
    ago(value) {
      const d = new Date(`${value}T00:00:00`);
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const days = Math.round((today - d) / 86400000);
      if (days <= 0) return "Today";
      if (days === 1) return "Yesterday";
      if (days < 30) return `${days} days ago`;
      if (days < 365) return `${Math.round(days / 30)} mo ago`;
      return `${Math.floor(days / 365)} yr ago`;
    },
    async load(page = 1) {
      clearTimeout(this.timer);
      const requestId = ++this.requestId;
      this.loading = true;
      this.error = "";
      try {
        const { data } = await axios.get(`/doctors/${this.doctorId}/my-patients`, {
          params: { q: this.q.trim() || undefined, sort: this.sort, page, per_page: 12 },
        });
        if (requestId !== this.requestId) return;
        this.patients = data.patients;
        this.pagination = data.pagination;
        this.patients.forEach(this.loadImage);
        if (page > 1) window.scrollTo({ top: 0, behavior: "smooth" });
      } catch (error) {
        if (requestId !== this.requestId) return;
        this.patients = [];
        this.error = errorMessage(error, "Could not load patients.");
      } finally {
        if (requestId === this.requestId) this.loading = false;
      }
    },
    async loadImage(p) {
      if (!p.profile_path || this.images[p.patientid] !== undefined) return;
      this.images[p.patientid] = "";
      try {
        const { data } = await axios.get(`/patients/${p.patientid}/profile-image`, { responseType: "arraybuffer" });
        this.images[p.patientid] = URL.createObjectURL(new Blob([data]));
      } catch (error) {
        // initials are shown instead
      }
    },
    book(p) {
      this.$store.dispatch("updateAppointmentDraft", { doctorid: this.doctorId, patientid: p.patientid, doctorImage: this.doctorImage });
      this.$router.push({ path: `/booking/${this.doctorId}`, query: { from: "doctorDashboard" } });
    },
  },
  created() {
    this.load(1);
  },
  beforeUnmount() {
    clearTimeout(this.timer);
    Object.values(this.images).forEach(url => url && URL.revokeObjectURL(url));
  },
};
</script>

<style scoped>
.pts {
  font-size: 14px;
}

.pts-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
}

.pts-search {
  flex: 1 1 260px;
  max-width: none;
}

.pts-sort {
  width: auto;
  min-width: 160px;
  padding: 8px 10px;
}

.pts-count {
  font-size: 13px;
  white-space: nowrap;
}

.pts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
  transition: opacity 0.15s;
}

.pts-grid.is-loading {
  opacity: 0.6;
}

.pts-card {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.pts-card:hover {
  border-color: #b2ddff;
  box-shadow: 0 4px 8px -2px rgba(16, 24, 40, 0.08);
}

.pts-card__head {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.pts-avatar {
  width: 52px;
  height: 52px;
  font-size: 18px;
}

.pts-card__who {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.pts-name {
  color: var(--adm-ink);
  font-weight: 700;
  font-size: 15px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-decoration: none;
}

.pts-name:hover {
  color: var(--adm-primary-600);
}

.pts-next {
  align-self: flex-start;
  margin-top: 4px;
}

.pts-contact {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
}

.pts-contact__item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--adm-ink-2);
  text-decoration: none;
  min-width: 0;
}

.pts-contact__item span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pts-contact__item i {
  width: 14px;
  color: var(--adm-faint);
  font-size: 12px;
}

a.pts-contact__item:hover {
  color: var(--adm-primary-600);
}

.pts-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  margin: 0;
  border-top: 1px solid var(--adm-line-2);
  border-bottom: 1px solid var(--adm-line-2);
  padding: 10px 0;
}

.pts-stats > div {
  text-align: center;
}

.pts-stats > div + div {
  border-left: 1px solid var(--adm-line-2);
}

.pts-stats dt {
  font-size: 11px;
  font-weight: 600;
  color: var(--adm-muted);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.pts-stats dd {
  margin: 2px 0 0;
  font-size: 17px;
  font-weight: 700;
  color: var(--adm-ink);
}

.pts-stats dd.pts-last {
  font-size: 13px;
  line-height: 1.9;
}

.pts-warn {
  color: var(--adm-danger-700) !important;
}

.pts-reason {
  margin: 0;
  font-size: 13px;
  color: var(--adm-ink-2);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pts-reason i {
  color: var(--adm-faint);
  margin-right: 6px;
}

.pts-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-top: auto;
}

.pts-pagination {
  margin-top: 16px;
  border-top: 0;
  padding: 0;
}
</style>
